import { useCallback, useEffect } from 'react';
import { useWizard } from '../../../components/wizard/useWizard';
import { Form } from 'antd';
import { PreWorkStepType } from './PreWorkStepType';
import { PreWorkQuestionId } from '../../../shared/Constants';

export default function usePreWorkFormHandling(
	categoryValue: PreWorkStepType,
	setCategoryValue: (value: PreWorkStepType) => void
) {
	const { setSubmitButtonDisabled, setCancelButtonDisabled } = useWizard();
	const [form] = Form.useForm<PreWorkStepType>();
	const values = Form.useWatch<PreWorkStepType>([], form);
	const { setFieldValue } = form;

	const setSystemGeneratedStateOfPreWork = useCallback(() => {
		if (!values) {
			return;
		}
		const selectedOptions = Object.values(values)
			.slice(0, 7)
			.map(
				(item: {
					selectedOption: number | undefined;
					comments: string | undefined;
				}) => {
					if (item?.selectedOption) {
						return item.selectedOption;
					}
					return 0;
				}
			);

		const heartfulCommunicationQuestionId =
			PreWorkQuestionId.HEARTFUL_COMMUNICATION;
		const systemGeneratedQuestionId = PreWorkQuestionId.SYSTEM_GENERATED;

		const completedAnswersCount = selectedOptions.filter(
			(option) => option === 1
		).length;
		const incompleteAnswersCount = selectedOptions.filter(
			(option) => ![1, 2].includes(option)
		).length;

		//As per the mentioned User Story, https://myjira.sahajmarg.org/help/browse/SNP-567 Following logics written.
		const isFullyCompleted =
			selectedOptions.every((option) => option === 1) ||
			(completedAnswersCount === 7 &&
				values[heartfulCommunicationQuestionId].selectedOption === 2);
		const isPartiallyCompleted =
			(completedAnswersCount >= 3 ||
				(completedAnswersCount >= 2 &&
					values[`${PreWorkQuestionId.HEARTFUL_COMMUNICATION}`]
						.selectedOption === 2)) &&
			incompleteAnswersCount === 0;

		let systemGeneratedValue: number;

		if (isFullyCompleted) {
			systemGeneratedValue = 1;
		} else if (isPartiallyCompleted) {
			systemGeneratedValue = 2;
		} else {
			systemGeneratedValue = 3;
		}

		setFieldValue(systemGeneratedQuestionId, {
			selectedOption: systemGeneratedValue,
		});
	}, [setFieldValue, values]);

	const handleFormValuesChange = useCallback(async () => {
		setSystemGeneratedStateOfPreWork();
		try {
			const fieldValues = await form.validateFields({ validateOnly: true });
			setCategoryValue(fieldValues);
			setSubmitButtonDisabled(false);
			if (!fieldValues.foundationOfServingIsIntegrity) {
				setSubmitButtonDisabled(true);
			}
		} catch (e) {
			const fields = e as { values: PreWorkStepType };
			setCategoryValue(fields.values);
			setSubmitButtonDisabled(true);
		}
		setCancelButtonDisabled(false);
	}, [
		form,
		setCancelButtonDisabled,
		setCategoryValue,
		setSubmitButtonDisabled,
		setSystemGeneratedStateOfPreWork,
	]);

	const updateFieldValue = useCallback(
		(questionId: number, value: number) => {
			form.setFieldsValue({
				[questionId]: { selectedOption: value },
			});
		},
		[form]
	);

	useEffect(() => {
		void handleFormValuesChange();
	}, [handleFormValuesChange, values]);

	useEffect(() => {
		form.setFieldsValue(categoryValue);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form, categoryValue]);

	return { form, updateFieldValue };
}
